import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { AccessGroup } from '@/shared/access';
// Deep import, not the `@/features/access` barrel: that barrel pulls in the feature's route table, which
// imports this very page — a cycle. `hooks` reaches only its own api layer and `shared/`, so importing it
// directly keeps the dependency one-way.
import { useDepartmentMemberships, useSetUserGroups } from '@/features/access/hooks';
import type { NormalizedApiError } from '@/shared/api';
import { ErrorState, LoadingState } from '@/shared/ui';

import type { Employee } from '../../employeesApi';

export interface UserGroupsDrawerProps {
  /** The person whose departments are shown. `null` closes the drawer. */
  employee: Employee | null;
  onClose: () => void;
}

/** Ancestor ids of a department, walked up the `parentId` chain through the flat group list. */
function ancestorIdsOf(group: AccessGroup, byId: Map<string, AccessGroup>): string[] {
  const ancestors: string[] = [];
  let current = group.parentId ? byId.get(group.parentId) : undefined;
  // Bounded by the map size: a corrupted parent chain must not hang the drawer.
  const seen = new Set<string>([group.id]);
  while (current && !seen.has(current.id)) {
    ancestors.push(current.id);
    seen.add(current.id);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return ancestors;
}

/**
 * One person's departments, edited beside the staff list rather than group-by-group.
 *
 * <p><b>Why here and not only on the Groups screen.</b> Membership is authored group-first everywhere
 * else: to answer "which departments is Anna in?" an administrator had to open each department in turn
 * and look for her name, and to move her they had to edit two rosters without ever seeing her as a
 * subject. The question staff administration actually asks is person-first, so this panel answers it in
 * that shape — while the writes underneath stay group-first, because that is the only membership API the
 * platform has.
 *
 * <p><b>Administrators only, by the server's rule not ours.</b> Every access endpoint behind this panel
 * is gated on `employees.manage` in the BFF, including the group reads. There is deliberately no
 * read-only mode: a view-only reader would get a panel of 403s, so the staff directory simply has no
 * departments column for them.
 *
 * <p><b>Direct membership, inherited effect.</b> The checkboxes are direct membership, which is what
 * Keycloak stores. But a department's grants apply to everyone below it too, so being in `/Sales/EMEA`
 * also confers `/Sales` — that is marked rather than left to be discovered, because ticking a child is
 * silently a broader decision than it looks.
 */
export function UserGroupsDrawer({ employee, onClose }: UserGroupsDrawerProps) {
  const { t } = useTranslation('employees');
  const open = employee !== null;

  // Nothing is fetched until the drawer is opened by someone who may read it: the roster costs one call
  // per department, and most visits to the staff list never open it.
  const memberships = useDepartmentMemberships({ enabled: open });
  const save = useSetUserGroups();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const currentIds = useMemo(
    () =>
      [...((employee ? memberships.byUser.get(employee.id) : undefined)?.map((g) => g.id) ?? [])].sort(),
    [employee, memberships.byUser],
  );
  /*
   * The effect keys off the membership's *content*, not the array holding it.
   *
   * `useDepartmentMemberships` inverts a fresh `useQueries` result, so `byUser` — and anything derived
   * from it — is a new object on most renders even when the answer is identical. Keying the effect on
   * that identity does not merely lose a tick: it is an infinite loop. The effect calls `setSelected`,
   * the re-render produces another new `byUser`, the dependency compares unequal, the effect runs again.
   * Reverting this line hangs the render rather than failing an assertion — which is how it was found,
   * when the mutation check killed a test worker after 296 seconds instead of reporting a failure.
   *
   * The unit tests could not see it at first: their mock returned one stable Map from module scope, so
   * the churn the real hook produces never happened. The mock now rebuilds the Map on every call.
   */
  const currentKey = currentIds.join(',');

  useEffect(() => {
    setSelected(new Set(currentKey ? currentKey.split(',') : []));
    setError(null);
  }, [currentKey, employee?.id]);

  const byId = useMemo(
    () => new Map(memberships.groups.map((group) => [group.id, group])),
    [memberships.groups],
  );

  /** Departments the person reaches through a child they are in — shown, never ticked. */
  const inherited = useMemo(() => {
    const ids = new Set<string>();
    for (const groupId of selected) {
      const group = byId.get(groupId);
      if (group) ancestorIdsOf(group, byId).forEach((id) => ids.add(id));
    }
    return ids;
  }, [selected, byId]);

  const added = useMemo(
    () => [...selected].filter((id) => !currentIds.includes(id)),
    [selected, currentIds],
  );
  const removed = useMemo(
    () => currentIds.filter((id) => !selected.has(id)),
    [selected, currentIds],
  );
  const dirty = added.length > 0 || removed.length > 0;

  const toggle = (group: AccessGroup) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(group.id)) next.delete(group.id);
      else next.add(group.id);
      return next;
    });
  };

  const submit = () => {
    if (!employee || !dirty) return;
    setError(null);
    save.mutate(
      { userId: employee.id, add: added, remove: removed },
      {
        onSuccess: () => onClose(),
        onError: (failure: NormalizedApiError) =>
          setError(failure.message || t('groups.saveFailed')),
      },
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={save.isPending ? undefined : onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 460 }, maxWidth: '100%' } } }}
    >
      {employee && (
        <Stack sx={{ height: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ p: 2, pb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {t('groups.title')}
              </Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {employee.name ?? employee.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {employee.email}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label={t('groups.close')}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1.5 }}>
            {memberships.isLoading ? (
              <LoadingState label={t('groups.loading')} />
            ) : memberships.isError ? (
              <ErrorState
                title={t('groups.loadError')}
                message={(memberships.error as NormalizedApiError | null)?.message ?? ''}
                onRetry={memberships.refetch}
              />
            ) : memberships.groups.length === 0 ? (
              <Alert severity="info" sx={{ mx: 1 }}>
                {t('groups.noDepartments')}
              </Alert>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block' }}>
                  {t('groups.hint')}
                </Typography>
                <List dense>
                  {memberships.groups.map((group) => {
                    const isMember = selected.has(group.id);
                    const isInherited = !isMember && inherited.has(group.id);
                    // The reserved All-staff group holds everyone by definition; Keycloak refuses edits
                    // to it, so offering a checkbox would be offering a request that always fails.
                    const locked = group.builtIn;
                    const indent = 2 + (group.depth ?? 0) * 2;

                    const label = (
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <span>{group.name}</span>
                        {locked && (
                          <Chip size="small" variant="outlined" label={t('groups.everyone')} />
                        )}
                        {isInherited && (
                          <Tooltip title={t('groups.inheritedHint')}>
                            <Chip
                              size="small"
                              variant="outlined"
                              color="info"
                              label={t('groups.inherited')}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    );

                    if (locked) {
                      return (
                        <ListItem key={group.id} sx={{ pl: indent }}>
                          <ListItemText
                            primary={label}
                            secondary={group.description}
                            slotProps={{ primary: { component: 'div' } }}
                          />
                          {(isMember || locked) && (
                            <Chip size="small" color="primary" label={t('groups.member')} />
                          )}
                        </ListItem>
                      );
                    }

                    return (
                      <ListItemButton
                        key={group.id}
                        onClick={() => toggle(group)}
                        sx={{ pl: indent }}
                        disabled={save.isPending}
                      >
                        <Checkbox
                          edge="start"
                          checked={isMember}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{
                            'aria-label': t('groups.toggleAria', { department: group.name }),
                          }}
                        />
                        <ListItemText
                          primary={label}
                          secondary={group.description}
                          slotProps={{ primary: { component: 'div' } }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </>
            )}
          </Box>

          {memberships.groups.length > 0 && (
            <>
              <Divider />
              <Stack spacing={1} sx={{ p: 2 }}>
                {error && <Alert severity="error">{error}</Alert>}
                {dirty && (
                  <Typography variant="caption" color="text.secondary">
                    {t('groups.pending', { added: added.length, removed: removed.length })}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button onClick={onClose} disabled={save.isPending}>
                    {t('groups.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={submit}
                    disabled={!dirty || save.isPending}
                  >
                    {t('groups.save')}
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
